import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import isTodayDayJs from 'dayjs/plugin/isToday';
dayjs.extend(relativeTime);
dayjs.extend(isTodayDayJs);

export const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

let localServerTimeDiff = 0;
export const MILLI_IN_MIN = 60000;

export const getMonth = (date: Date): string => {
  return monthNames[date.getMonth()];
};

export const getWeekDay = (date: Date) => {
  return weekday[date?.getDay()];
};

export const getFormattedDateMonth = (date: Date): string => {
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  return day + ' ' + month;
};

export const getFormattedDateWithTime = (date: Date): string => {
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const time = date.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
  return `${day} ${month}, ${year}, ${time}`;
};
export const getFormattedDateWithFullYear = (date: Date): string => {
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

export const getFormattedDateTime = (date: Date): string => {
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const year = String(date.getFullYear()).slice(-2);
  const time = date.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
  return `${day} ${month}, ${year} | ${time}`;
};

export const getCustomFormattedDate = (date: Date): string => {
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const year = String(date.getFullYear()).slice(-2);
  const time = date.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });

  return `${day} ${month}' ${year}`;
};

export const getFormattedDateWithYearAndTime = (date: Date): string => {
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const time = date
    .toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true })
    .toLocaleLowerCase();
  return `${day} ${month}, ${year} | ${time}`;
};

export const getFormattedTime = (date: Date): string => {
  const formattedDate = dayjs(date).format(DateFormat.hh_mm_ss_ampm);
  return formattedDate;
};

export enum DateFormat {
  ISO_DATE_FORMAT = 'YYYY-MM-DD',
  DD_MM_YYYY = 'DD-MM-YYYY',
  YYYY_MM_DD = 'YY-MM-DD',
  DD_MMM = 'DD MMM',
  DD_MMM_HH_mm = 'DD MMM | HH:mm',
  DD_MMM_YY = 'DD MMM’YY',
  DD_MMM_YYYY_HH_MM = 'DD MMM YYYY HH:mm ampm',
  DD_MMM_YYYY = 'DD_MMM_YYYY',
  HH_mm_ampm = 'HH:mm ampm',
  DATE_PICKER_FORMAT = 'YYYY-MM-DD',
  LONG_DATE_FORMAT_WITH_TIME = 'DD MMM, YYYY | HH:mm ampm',
  LONG_DATE_FORMAT_WITHOUT_TIME = 'DD MMM, YYYY',
  FILE_DATE_FORMAT = 'DD_MMM_YYYY_HH_mm_ampm',
  YYYY = 'YYYY',
  DD_MMM_YYYY_hh_mm_a = 'DD MMM, YYYY | hh:mm a',
  HH_mm_a = 'hh:mm a',
  HH_mm_A = 'HH:mm A',
  DAY = 'dddd',
  COMMUNICATION_HISTORY_DATE_FORMAT = 'YYYY-MM-DDTHH:mm',
  DD_MMM_hh_mm_A = 'DD MMM, hh:mm A',
  LONG_DATE_FORMAT_WITH_TIME_A = 'DD MMM, YYYY | hh:mm A',
  LONG_DATE_WITH_DAY = 'DD MMM, YYYY (dddd)',
  hh_mm_ss = 'hh:mm:ss',
  hh_mm_ss_ampm = 'hh:mm:ss a',
  DD_MMM_YYYY__hh_mm_a = 'DD MMM YYYY, HH:mm a'
}

export const utcToIST = (inputDate: Date) => {
  const istNow = new Date();
  const utcNow = new Date(inputDate);
  istNow.setTime(utcNow.getTime() + 5.5 * 60 * 60 * 1000);
  return istNow;
};

/**
 * This function is deprecated please use dateFormat isntead
 * @param inputDate is a date object in Date, number or string format
 */
export const getFormatDate = (inputDate: Date | number | string, format: DateFormat): string => {
  if (!inputDate) return 'INVALID DATE';
  const date = new Date(inputDate);
  switch (format) {
    case DateFormat.YYYY_MM_DD:
      return date.toISOString().split('T')[0];
    case DateFormat.DD_MMM:
      return getFormattedDateMonth(date);
    case DateFormat.DD_MMM_HH_mm:
      return getFormattedDateTime(date);
    case DateFormat.DD_MMM_YY:
      return getCustomFormattedDate(date);
    case DateFormat.DD_MMM_YYYY_HH_MM:
      return getFormattedDateWithTime(date);
    case DateFormat.DD_MMM_YYYY:
      return getFormattedDateWithFullYear(date);
    case DateFormat.DD_MMM_YYYY_hh_mm_a:
      return getFormattedDateWithYearAndTime(date);
    case DateFormat.DD_MMM_YYYY__hh_mm_a: {
      const month = monthNames[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      const time = date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      });
      return `${day} ${month} ${year}, ${time}`;
    }
    case DateFormat.LONG_DATE_FORMAT_WITHOUT_TIME:
      return getFormattedDateWithFullYear(date);
    case DateFormat.hh_mm_ss_ampm:
      return getFormattedTime(date);
    default:
      return 'INVALID FORMAT';
  }
};

export enum UnitsEnum {
  Seconds = 'seconds',
  Minutes = 'minutes',
  Hours = 'hours',
  Days = 'days',
  Months = 'months',
  Years = 'years'
}

export const MilliSecondsTo = {
  [UnitsEnum.Seconds]: (ms: number) => ms / 1000,
  [UnitsEnum.Minutes]: (ms: number) => Math.round(MilliSecondsTo.seconds(ms) / 60),
  [UnitsEnum.Hours]: (ms: number) => Math.round(MilliSecondsTo.minutes(ms) / 60),
  [UnitsEnum.Days]: (ms: number) => Math.round(MilliSecondsTo.hours(ms) / 24),
  [UnitsEnum.Months]: (ms: number) => Math.round(MilliSecondsTo.days(ms) / 30),
  [UnitsEnum.Years]: (ms: number) => Math.round(MilliSecondsTo.months(ms) / 12)
};

/**
 * Finds the difference between start and end dates in terms of the units specified
 * @param start
 * @param end
 * @param units
 * @returns Be careful with this method, since it is implemented using Math.Round and may produce unexpected results
 */
export const dateDiff = (start: Date, end: Date, units: UnitsEnum) => {
  const diff = end.getTime() - start.getTime();
  return MilliSecondsTo[units](diff);
};

export const destructureDate = (date: Date) => {
  const year = date.getFullYear().toString();
  const month = date.getMonth();
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  return {
    year,
    month,
    day,
    hours,
    minutes,
    seconds
  };
};

export const dateFormat = (date: Date, format: string) => {
  const { year, month, day, hours, minutes, seconds } = destructureDate(date);

  return format.replace(/YYYY|MMMM|MMM|DDDD|DDD|YY|MM|dd|DD|HH|hh|mm|ss|ampm/g, match => {
    switch (match) {
      case 'YYYY':
        return year.toString();
      case 'MMMM':
        return date.toLocaleDateString(undefined, { month: 'long' });
      case 'MMM':
        return date.toLocaleDateString(undefined, { month: 'short' });
      case 'DDDD':
        return date.toLocaleDateString(undefined, { weekday: 'long' });
      case 'DDD':
        return date.toLocaleDateString(undefined, { weekday: 'short' });
      case 'YY':
        return year.substring(2);
      case 'MM':
        return (month + 1).toString().padStart(2, '0');
      case 'dd':
        return day.toString();
      case 'DD':
        return day.toString().padStart(2, '0');

      case 'HH':
        return ((hours + 24) % 12 || 12).toString().padStart(2, '0');
      case 'hh':
        return hours.toString().padStart(2, '0');
      case 'mm':
        return minutes.toString().padStart(2, '0');
      case 'ss':
        return seconds.toString().padStart(2, '0');

      case 'ampm':
        return hours < 12 ? 'am' : 'pm';

      default:
        return match;
    }
  });
};

export const calcultateDateDeltaMinutes = (inputDate: Date, minuteWithDelta: number) => {
  return new Date(new Date(inputDate).getTime() + minuteWithDelta * 60 * 1000);
};

export const getServerTime = () => {
  return localServerTimeDiff + Date.now();
};

export const setLocalServerTimeDiff = (diff: number) => {
  if (localServerTimeDiff) return;
  localServerTimeDiff = diff;
};

/**
 * Finds the difference between start and end dates in terms of the units specified
 * @param Date1
 * @param Date2
 * @returns Date2 - Date 1 days diff
 */
export const calculateDateDifferenceInDays = (
  date1: Date,
  date2: Date,
  doNotConsiderToday?: boolean
) => {
  const diff = date2.getTime() - date1.getTime();
  const days = Math.ceil(diff / (1000 * 3600 * 24));
  return doNotConsiderToday ? days - 1 : days;
};

export const incrementDateByMins = (inputDate: Date, mins: number) => {
  const dateModified = new Date();
  const parsedDate = new Date(inputDate);
  dateModified.setTime(parsedDate.getTime() + mins * 60 * 1000);
  return dateModified;
};

export const isToday = (lastUpdatedAt: string) => {
  return dayjs(lastUpdatedAt)?.isToday?.();
};

export const formatDate = (lastUpdatedAt: string, format: string) => {
  if (!lastUpdatedAt) return '';
  return dayjs(lastUpdatedAt)?.format?.(format);
};

export const formatEpochTime = (timestamp: number) => {
  return dayjs.unix(timestamp / 1000);
};

export const to12Hours = (hour: number) => {
  if (hour === 0) return 12;
  return hour > 12 ? hour - 12 : hour;
};

export function formatFeedbackTime(time: string) {
  if (!time) return;
  const timeParts = time.split(':');
  const feedbackTime = new Date();
  feedbackTime.setHours(parseInt(timeParts[0]));
  feedbackTime.setMinutes(parseInt(timeParts[1]));
  return dateFormat(feedbackTime, DateFormat.HH_mm_ampm);
}

export const getPreviousDate = (days: number) => {
  const date = new Date();
  date.setDate(new Date().getDate() - days);
  return date;
};

export const formatDateRange = (days: number): string => {
  const endDate = new Date();
  const startDate = new Date();

  startDate.setDate(endDate.getDate() - days);

  return `${getFormatDate(startDate, DateFormat.DD_MMM)} - ${getFormatDate(
    endDate,
    DateFormat.DD_MMM
  )}`;
};

export const getFormattedDateSubtractedByDays = (days: number): string => {
  return dayjs()?.subtract(days, 'day')?.format(DateFormat.ISO_DATE_FORMAT);
};

export const timeStringToDate = (time?: string): Date => {
  const now = new Date();
  if (!time) return now;
  const [hours, minutes, seconds] = time.split(':').map(Number);
  now.setHours(hours, minutes, seconds, 0);
  return now;
};

export const formatToStandardTimeInHours = (time?: string) => {
  if (!time) return '';
  const [hours] = time.split(':');
  const hour = parseInt(hours);
  return `${hour === 12 ? 12 : hour % 12}${hour >= 12 ? 'PM' : 'AM'}`;
};
